import { observer } from 'mobx-react-lite'
import React, { useContext, useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { InfluencerContext } from '../../../../contexts/InfluencerContext'
import { SharedCampaignContext } from '../../../../contexts/Advertiser/SharedCampaignContext'
import { SharedInfluencerContext } from '../../../../contexts/Advertiser/SharedInfluencerContext'
import { providerLogo } from '../../../../utils/Helper'
import { Plus } from 'react-bootstrap-icons'
import SedcardModal from './OverviewParts/SedcardModal'
import SortSelect from '../../../../components/FormComponents/SortSelect'
import Authentication from '../../../../models/Authentication'
import axiosService from '../../../../utils/AxiosService'
import { toastError, toastSuccess } from '../../../../components/FormComponents/Toasts'

export default observer(function Providers({ isAdvertiserContext = false, isInfluencerShareContext = false }) {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const { id, campaign_authentication_id, authentication_id, uuid } = params
  const influencerContext = useContext(InfluencerContext)
  const sharedCampaignContext = useContext(SharedCampaignContext)
  const sharedInfluencerContext = useContext(SharedInfluencerContext)
  const [sedcardOpen, setSedcardOpen] = useState(false)
  const [authentications, setAuthentications] = useState([])
  const [postsSortOrder, setPostsSortOrder] = useState('desc')

  // Get authentications based on context
  useEffect(() => {
    if (isAdvertiserContext && authentication_id) {
      axiosService.get(`/api/advertiser/campaign_share/authentications/${authentication_id}/authentications`)
        .then(authentications => {
          const mappedAuthentications = authentications.map(authentication => new Authentication(authentication))
          setAuthentications(mappedAuthentications)
        })
        .catch((error) => {
          const errorMessage = error?.response?.data?.error || error?.data?.error || error?.error || error?.message || 'An error occurred'
          toastError(errorMessage)
        })
    }
  }, [isAdvertiserContext, authentication_id])

  const pmAuthentications = influencerContext?.authentications || []
  const sharedInfluencerAuthentications = sharedInfluencerContext?.authentications || []
  const currentAuthentications = isInfluencerShareContext
    ? sharedInfluencerAuthentications
    : (isAdvertiserContext ? authentications : pmAuthentications)
  const currentId = isInfluencerShareContext
    ? (sharedInfluencerAuthentications[0]?.id)
    : (isAdvertiserContext ? authentication_id : id)

  const getCurrentPath = () => {
    if (location.pathname.includes('statistics')) return 'statistics'
    if (location.pathname.includes('social_posts')) return 'social_posts'
    return 'overview'
  }

  const selectAuthentication = authentication => () => {
    if (isInfluencerShareContext) {
      // In influencer share context, we only have one authentication, so just navigate to different pages
      const currentPath = getCurrentPath()
      navigate(`/advertiser/influencers/shared/${uuid}/${currentPath}`)
    } else if (isAdvertiserContext) {
      const currentPath = getCurrentPath()
      const campaignId = params.id
      navigate(`/advertiser/campaign_share/${campaignId}/influencers/campaign_authentications/${campaign_authentication_id}/authentication/${authentication.id}/${currentPath}`)
    } else {
      const activePath = influencerContext?.activePath || 'overview'
      navigate(`/project_manager/authentications/${authentication.id}/influencer/${activePath}`)
    }
  }
  const currentAuthentication = currentAuthentications.find(auth => +auth.id === +currentId)

  const handleSortChange = (selectedOption) => {
    const newSortOrder = selectedOption.value
    setPostsSortOrder(newSortOrder)
    if (isInfluencerShareContext && sharedInfluencerContext?.setPostsSortOrder) {
      sharedInfluencerContext.setPostsSortOrder(newSortOrder)
    } else if (!isAdvertiserContext && influencerContext?.getPosts) {
      if (currentAuthentication) {
        influencerContext.getPosts(currentAuthentication, newSortOrder)
      }
    }
  }

  const getProviderColor = provider => ({
    instagram: 'text-pink-600',
    tiktok: '',
    youtube: 'text-red-500',
    linkedin: 'text-rb-linkedin'
  }[provider])

  const copySharingUrlToClipboard = () => {
    const sharingLink = `${window.location.origin}/advertiser/influencers/shared/${currentAuthentication.shared_uuid}/`
    navigator.clipboard.writeText(sharingLink);
    toastSuccess('Link copied to clipboard')
  }

  return (
    <div className="flex items-center justify-between bg-rb-grey dark:bg-rb-dark-mode-background pt-4 pr-8">
      <div className="flex items-center">
        {currentAuthentications.map(authentication =>
          <button
            key={authentication.id}
            onClick={selectAuthentication(authentication)}
            className={`flex items-center p-3 rounded-2xl ml-4 border-1 ${+currentId === +authentication.id ? 'bg-rb-soft-green border-rb-green dark:bg-rb-green/20 dark:border-rb-green' : 'bg-rb-grey dark:bg-rb-dark-mode-lighter border-widget-border dark:border-rb-dark-mode-lighter hover:bg-gray-100 dark:hover:bg-rb-dark-mode'}`}
          >
            <span className={`${getProviderColor(authentication.provider)} ${authentication.provider === 'instagram' ? 'dark:text-pink-400' : ''} ${authentication.provider === 'youtube' ? 'dark:text-red-400' : ''} ${authentication.provider === 'linkedin' ? 'dark:text-rb-linkedin' : ''} ${authentication.provider === 'tiktok' ? 'dark:text-white' : ''}`}>{providerLogo(authentication.provider)}</span>
            <span className={`ml-2 capitalize dark:text-white`}>{authentication.provider}</span>
          </button>
        )}
        <div className="ml-4 w-48">
          <SortSelect
            value={isInfluencerShareContext
              ? (sharedInfluencerContext?.postsSortOrder || 'desc')
              : (isAdvertiserContext ? postsSortOrder : (influencerContext?.postsSortOrder || 'desc'))}
            onChange={handleSortChange}
          />
        </div>
      </div>
      {!isAdvertiserContext && !isInfluencerShareContext && (
        <div className="flex items-center">
          <button
            onClick={() => copySharingUrlToClipboard()}
            className="flex items-center border-1 border-border-primary dark:border-rb-dark-mode-lighter text-gray-800 dark:text-white p-2 px-3 rounded-md mr-2 bg-white dark:bg-rb-dark-mode hover:bg-gray-100 dark:hover:bg-rb-dark-mode-lighter"
          >
            Share link
          </button>
          <button
            onClick={() => setSedcardOpen(true)}
            className="flex items-center border-1 border-border-primary dark:border-rb-dark-mode-lighter text-gray-800 dark:text-white p-2 px-3 rounded-md mr-2 bg-white dark:bg-rb-dark-mode hover:bg-gray-100 dark:hover:bg-rb-dark-mode-lighter"
          >
            Sedcard
          </button>
          <button
            onClick={() => influencerContext?.openAddAuthenticationModal?.('pull')}
            className="flex items-center bg-rb-green text-white p-2 px-3 rounded-md dark:text-rb-dark-mode"
          >
            <span className="">
              <Plus className="w-6 h-6 text-white dark:text-rb-dark-mode" />
            </span>
            Add platform
          </button>
        </div>
      )}
      {!isAdvertiserContext && !isInfluencerShareContext && <SedcardModal isOpen={sedcardOpen} onClose={() => setSedcardOpen(false)} />}
    </div>
  )
})